import { createContext, useState, useContext, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { loginUser, setUserSession, getUserData, clearUserSession, isAuthenticated } from "../services/authService";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Logout function
  const logout = useCallback(() => {
    clearUserSession();
    setUser(null);
    navigate("/login", { replace: true });
  }, [navigate]);

  // Verify token on initial load
  useEffect(() => {
    const verifySession = () => {
      if (isAuthenticated()) {
        try {
          const userData = getUserData();
          setUser(userData);
        } catch (error) {
          console.error("Error parsing user data", error);
          logout();
        }
      }
      setLoading(false);
    };

    verifySession();
  }, [logout]);

  // Login function
  const login = async (contactNumber, password) => {
    try {
      const response = await loginUser(contactNumber, password);

      if (response.success) {
        // Store user data and token in localStorage
        setUserSession(response.result);
        
        // Update state
        setUser(response.result);
        return { success: true, user: response.result };
      } else {
        return { success: false, message: response.message };
      }
    } catch (error) {
      console.error("Login failed:", error);
      const errorMessage = error.message || "Login failed. Please try again.";
      return { success: false, message: errorMessage };
    }
  };

  // Show loading state while verifying auth
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <div>Loading authentication...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      logout,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};